import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import "../styles/sidebar.css";
import { useAuth } from "../Contexts/AuthContext";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faUserTie,
  faClipboardCheck,
  faUsers,
  faTasks,
  faChartLine,
  faCalendarDays,
  faRightFromBracket,
  faUserInjured,
  faBoxOpen,
  faWarehouse,
  faShoppingCart,
  faUserMd,
  faHospital,
  faFileMedical,
  faCapsules,
  faUserGraduate,
  faChalkboard,
  faMoneyBill,
  faBook,
  faSquare,
  faFileInvoiceDollar,
  faBriefcase,
  faProjectDiagram,
  faWallet,
  faPercentage,
  faIndustry,
  faCheckDouble,
  faTools,
  faGavel,
  faFolderOpen,
  faHourglassHalf,
  faShieldHalved,
  faCommentDots,
  faShareNodes,
  faPhoneVolume,
  faBoxesStacked,
  faLayerGroup,
  faFunnelDollar,
  faBus,
  faBullhorn,
  faGraduationCap,
  faCalendarCheck,
  faBed,
  faAngleLeft,
  faAngleRight,
  faAngleDown,
  faComments,
  faHandshakeAngle,
  faShieldAlt,
  faShareAlt,
  faClipboardList,
  faBuilding,
  faAddressCard,
  faCubes,
  faWrench,
  faClock,
  faAward,
  faVideo
} from "@fortawesome/free-solid-svg-icons";

// Map backend icon strings to FontAwesome objects
const iconMapping = {
  faAward,
  faHome,
  faUserTie,
  faClipboardCheck,
  faUsers,
  faTasks,
  faChartLine,
  faCalendarDays,
  faRightFromBracket,
  faUserInjured,
  faBoxOpen,
  faWarehouse,
  faShoppingCart,
  faUserMd,
  faHospital,
  faFileMedical,
  faCapsules,
  faUserGraduate,
  faChalkboard,
  faMoneyBill,
  faBook,
  faFileInvoiceDollar,
  faBriefcase,
  faProjectDiagram,
  faWallet,
  faPercentage,
  faIndustry,
  faCheckDouble,
  faTools,
  faGavel,
  faFolderOpen,
  faHourglassHalf,
  faShieldHalved,
  faCommentDots,
  faShareNodes,
  faPhoneVolume,
  faBoxesStacked,
  faLayerGroup,
  faFunnelDollar,
  faBus,
  faBullhorn,
  faGraduationCap,
  faCalendarCheck,
  faBed,
  faComments,
  faHandshakeAngle,
  faShieldAlt,
  faShareAlt,
  faClipboardList,
  faBuilding,
  faAddressCard,
  faCubes,
  faWrench,
  faClock,
  faVideo,
};

export default function Sidebar({ isCollapsed, onToggle, isMobileOpen, onCloseMobile }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, navigation, onboardingComplete } = useAuth();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isMobileOpen !== undefined ? isMobileOpen : internalOpen;
  const setOpen = (val) => {
    const nextVal = typeof val === "function" ? val(open) : val;
    if (onCloseMobile && !nextVal) {
      onCloseMobile();
    }
    setInternalOpen(nextVal);
  };
  const [searchQuery, setSearchQuery] = useState("");

  const [expandedTags, setExpandedTags] = useState(() => {
    try {
      const saved = localStorage.getItem("dis_sidebar_expanded_tags");
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  const [expandedSubtags, setExpandedSubtags] = useState(() => {
    try {
      const saved = localStorage.getItem("dis_sidebar_expanded_subtags");
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  const toggleTag = (tag) => {
    setExpandedTags((prev) => {
      const isExpanded = prev[tag] !== false;
      const next = { ...prev, [tag]: !isExpanded };
      localStorage.setItem("dis_sidebar_expanded_tags", JSON.stringify(next));
      return next;
    });
  };

  const toggleSubtag = (tag, subTag) => {
    const key = `${tag}:${subTag}`;
    setExpandedSubtags((prev) => {
      const isExpanded = prev[key] !== false;
      const next = { ...prev, [key]: !isExpanded };
      localStorage.setItem("dis_sidebar_expanded_subtags", JSON.stringify(next));
      return next;
    });
  };

  const effectiveUser = user || (() => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  })();

  const effectiveNavigation = (Array.isArray(navigation) && navigation.length > 0)
    ? navigation
    : (() => {
        try {
          const stored = localStorage.getItem("navigation");
          return stored ? JSON.parse(stored) : [];
        } catch (e) {
          return [];
        }
      })();

  const effectiveOnboardingComplete = onboardingComplete !== undefined && onboardingComplete !== null
    ? onboardingComplete
    : (() => {
        try {
          const stored = localStorage.getItem("onboardingComplete");
          return stored !== null ? JSON.parse(stored) : true;
        } catch (e) {
          return true;
        }
      })();

  const filteredNavigation = effectiveNavigation.filter(item => 
    item.codename !== 'dashboard' && 
    item.codename !== 'education_dashboard' && 
    item.codename !== 'ngo_dashboard' && 
    item.codename !== 'meetings' && 
    item.path !== '/meetings' && 
    item.codename !== 'leads' && (
      (item.name && item.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.tag && item.tag.toLowerCase().includes(searchQuery.toLowerCase()))
    )
  );

  // Auto-expand the active section on mount or navigation change
  useEffect(() => {
    const activeItem = filteredNavigation.find(item => location.pathname === item.path);
    if (activeItem) {
      const tag = activeItem.tag || "General";
      const subTag = activeItem.sub_tag || "Core";
      const subKey = `${tag}:${subTag}`;
      
      let updatedTags = null;
      let updatedSubtags = null;

      if (expandedTags[tag] === false) {
        updatedTags = { ...expandedTags, [tag]: true };
      }
      if (expandedSubtags[subKey] === false) {
        updatedSubtags = { ...expandedSubtags, [subKey]: true };
      }

      if (updatedTags) {
        setExpandedTags(updatedTags);
        localStorage.setItem("dis_sidebar_expanded_tags", JSON.stringify(updatedTags));
      }
      if (updatedSubtags) {
        setExpandedSubtags(updatedSubtags);
        localStorage.setItem("dis_sidebar_expanded_subtags", JSON.stringify(updatedSubtags));
      }
    }
  }, [location.pathname, filteredNavigation]);
  
  // Auto-scroll to active item on mount or navigation change
  useEffect(() => {
    const activeItem = document.querySelector(".sb-item.active");
    if (activeItem) {
      activeItem.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [location.pathname, effectiveNavigation]);

  // Helper to get the correct icon component
  const getIcon = (iconName) => {
    return iconMapping[iconName] || faSquare;
  };

  const isSuperadmin = effectiveUser?.is_superuser || effectiveUser?.rbac_roles_details?.some(r => r.name === 'Superadmin');
  const orgHasMeetings = Boolean(effectiveUser?.organization_details?.has_meetings_module);
  const hasMeetingModule = isSuperadmin || orgHasMeetings || (Array.isArray(effectiveNavigation) && effectiveNavigation.some(
    (item) => item?.codename === "meetings" || item?.path === "/meetings"
  ));

  const orgName = isSuperadmin ? "Global Workspace" : (effectiveUser?.organization_details?.name || "Global Workspace");
  const industryName = isSuperadmin ? "Superadmin" : (effectiveUser?.organization_details?.industry_name || "Superadmin");
  const avatarText = isSuperadmin ? "GLO" : orgName.substring(0, 3).toUpperCase();

  return (
    <>
      <aside className={`sb-aside ${open ? "open" : ""} ${isCollapsed ? "collapsed" : ""}`}>
        {/* Floating border collapse toggle */}
        <button 
          type="button" 
          onClick={onToggle}
          className="sb-collapse-btn"
          style={{ display: open ? 'none' : 'flex' }}
        >
          <FontAwesomeIcon icon={isCollapsed ? faAngleRight : faAngleLeft} />
        </button>

        {/* Logo */}
        <div className="sb-logo-wrap">
          <div className="sb-logo-row">
            <div className="sb-logo-icon">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
              </svg>
            </div>
            {!isCollapsed && (
              <div>
                <div className="sb-logo-name">DoneInSeconds</div>
                <div className="sb-logo-sub">Business Platform</div>
              </div>
            )}
          </div>

          <div className="sb-workspace">
            <div className="sb-workspace-left" style={{ width: '100%' }}>
              <div className="sb-ws-avatar">{avatarText}</div>
              <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <span className="sb-ws-name" title={orgName}>{orgName}</span>
                <span style={{ fontSize: '10px', opacity: 0.6 }}>{industryName}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="sb-search-container">
          <div className="sb-search-box">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="sb-search-icon"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="search"
              name="dis_sidebar_search_query"
              id="dis_sidebar_search_query"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-lpignore="true"
              data-1p-ignore="true"
              data-bwignore="true"
              placeholder="Search modules..."
              className="sb-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && filteredNavigation.length > 0) {
                  const firstMatch = filteredNavigation[0];
                  navigate(firstMatch.path);
                  setOpen(false);
                  setSearchQuery("");
                }
              }}
            />
            {searchQuery && (
              <button className="sb-search-clear" onClick={() => setSearchQuery("")}>
                ×
              </button>
            )}
          </div>
        </div>

        {/* Grouped Menu */}
        <nav className="sb-menu">
          {/* Default Dashboard & Assigned Home Modules */}
          {((!searchQuery || "dashboard".includes(searchQuery.toLowerCase()) || "overview".includes(searchQuery.toLowerCase())) || 
            (hasMeetingModule && (!searchQuery || "meetings".includes(searchQuery.toLowerCase()) || "video call".includes(searchQuery.toLowerCase())))) && (
            <div className="sb-category-group">
              <div className="sb-category-title">Home</div>
              {(!searchQuery || "dashboard".includes(searchQuery.toLowerCase()) || "overview".includes(searchQuery.toLowerCase())) && (
                <Link
                  to="/dashboard"
                  className={`sb-item ${location.pathname === "/dashboard" ? "active" : ""}`}
                  onClick={() => setOpen(false)}
                >
                  <div className="sb-item-icon">
                    <FontAwesomeIcon icon={faHome} />
                  </div>
                  <div className="sb-item-label">
                    <span className="sb-item-name">Dashboard</span>
                    <span className="sb-item-tag">Overview</span>
                  </div>
                </Link>
              )}

              {hasMeetingModule && (!searchQuery || "meetings".includes(searchQuery.toLowerCase()) || "video call".includes(searchQuery.toLowerCase())) && (
                <Link
                  to={!effectiveOnboardingComplete ? "#" : "/meetings"}
                  className={`sb-item ${location.pathname.startsWith("/meetings") ? "active" : ""} ${!effectiveOnboardingComplete ? "disabled" : ""}`}
                  onClick={(e) => {
                    if (!effectiveOnboardingComplete) {
                      e.preventDefault();
                      return;
                    }
                    setOpen(false);
                  }}
                  style={!effectiveOnboardingComplete ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                >
                  <div className="sb-item-icon">
                    <FontAwesomeIcon icon={faVideo} />
                  </div>
                  <div className="sb-item-label">
                    <span className="sb-item-name">Meetings</span>
                    <span className="sb-item-tag">{!effectiveOnboardingComplete ? "Setup Required" : "Video Call"}</span>
                  </div>
                </Link>
              )}
            </div>
          )}

          {Object.entries(
            filteredNavigation.reduce((acc, item) => {
              const tag = item.tag || "General";
              if (!acc[tag]) acc[tag] = {};
              const subTag = item.sub_tag || "Core";
              if (!acc[tag][subTag]) acc[tag][subTag] = [];
              acc[tag][subTag].push(item);
              return acc;
            }, {})
          ).map(([tag, subGroups]) => {
            const isTagExpanded = isCollapsed || !!searchQuery || expandedTags[tag] !== false;

            return (
              <div key={tag} className="sb-category-group">
                <div 
                  className={`sb-category-header ${!isTagExpanded ? "is-collapsed" : ""}`}
                  onClick={() => !isCollapsed && !searchQuery && toggleTag(tag)}
                  style={isCollapsed || searchQuery ? { cursor: 'default' } : {}}
                >
                  <div className="sb-category-title">{tag}</div>
                  {!isCollapsed && !searchQuery && (
                    <FontAwesomeIcon icon={faAngleDown} className="sb-category-chevron" />
                  )}
                </div>

                <div className={`sb-collapsible-wrapper ${isTagExpanded ? "is-expanded" : ""}`}>
                  <div className="sb-collapsible-inner">
                    {Object.entries(subGroups).map(([subTag, items]) => {
                      const subKey = `${tag}:${subTag}`;
                      const isSubtagExpanded = isCollapsed || !!searchQuery || expandedSubtags[subKey] !== false;
                      const hasSubtagHeader = subTag !== "Core" && subTag !== "General";

                      return (
                        <div key={subTag} className="sb-subtag-group" style={{ marginBottom: '6px' }}>
                          {hasSubtagHeader && (
                            <div 
                              className={`sb-subtag-header ${!isSubtagExpanded ? "is-collapsed" : ""}`}
                              onClick={() => !isCollapsed && !searchQuery && toggleSubtag(tag, subTag)}
                              style={isCollapsed || searchQuery ? { cursor: 'default' } : {}}
                            >
                              <div className="sb-subtag-title">{subTag}</div>
                              {!isCollapsed && !searchQuery && (
                                <FontAwesomeIcon icon={faAngleDown} className="sb-subtag-chevron" />
                              )}
                            </div>
                          )}

                          <div className={`sb-collapsible-wrapper ${!hasSubtagHeader || isSubtagExpanded ? "is-expanded" : ""}`}>
                            <div className="sb-collapsible-inner">
                              {items.map((item) => {
                                const isActive = location.pathname === item.path;
                                const isSetupModule = item.codename === 'organization_profile';
                                const isDisabled = !onboardingComplete && !isSetupModule && item.codename !== 'dashboard';

                                return (
                                  <Link
                                    key={item.codename}
                                    to={isDisabled ? "#" : item.path}
                                    className={`sb-item ${isActive ? "active" : ""} ${isDisabled ? "disabled" : ""}`}
                                    onClick={(e) => {
                                      if (isDisabled) {
                                        e.preventDefault();
                                        return;
                                      }
                                      setOpen(false);
                                    }}
                                    style={isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                  >
                                    <div className="sb-item-icon">
                                      <FontAwesomeIcon icon={getIcon(item.icon_name)} />
                                    </div>
                                    <div className="sb-item-label">
                                      <span className="sb-item-name">{item.name}</span>
                                      <span className="sb-item-tag">{isDisabled ? "Setup Required" : item.tag}</span>
                                    </div>
                                    {!isDisabled && <span className="sb-item-pill">{isActive ? "●" : "○"}</span>}
                                    {isDisabled && <FontAwesomeIcon icon={faSquare} style={{ fontSize: '8px', opacity: 0.3 }} />}
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}

          {/* System Section */}
          <div className="sb-category-group">
            <div className="sb-category-title">System</div>
            <Link
              to="/profile"
              className={`sb-item ${location.pathname === "/profile" ? "active" : ""}`}
              onClick={() => setOpen(false)}
            >
              <div className="sb-item-icon">
                <FontAwesomeIcon icon={faUserTie} />
              </div>
              <div className="sb-item-label">
                <span className="sb-item-name">My Profile</span>
                <span className="sb-item-tag">Account Settings</span>
              </div>
            </Link>
            <Link
              to="/logout"
              className="sb-item logout"
              onClick={() => setOpen(false)}
            >
              <div className="sb-item-icon">
                <FontAwesomeIcon icon={faRightFromBracket} />
              </div>
              <div className="sb-item-label">
                <span className="sb-item-name">Logout</span>
                <span className="sb-item-tag">Session</span>
              </div>
            </Link>
          </div>
        </nav>

        <hr className="sb-divider" />

        {/* User footer */}
        <div className="sb-footer">
          <Link to="/profile" className="sb-user" onClick={() => setOpen(false)}>
            <div className="sb-user-avatar">
              {user?.username?.substring(0, 1).toUpperCase() || "U"}
            </div>
            <div className="sb-user-info">
              <div className="sb-user-name">{user?.username || "User"}</div>
              <div className="sb-user-role">
                {user?.rbac_roles_details?.[0]?.name || user?.rbac_roles?.[0]?.name || "No Role"}
              </div>
            </div>
            <span className="sb-user-dots">···</span>
          </Link>
        </div>
      </aside>

      {open && <div className="sb-overlay" onClick={() => setOpen(false)} />}
    </>
  );
}